import { useContext, useState } from 'react';

import {
  PayPalButtons,
  PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js';

import { useAppDispatch } from '@/store/hooks';
import { updateUserDetailsFields } from '@/features/userDetails/userDetailsSlice';
import { logger } from '@/services/logger/logger';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { ProvidersLibsContext } from '@/services/providersLibs/providersLibsContext';
import { setComplete, clearErrorMessage, setErrorMessage } from '@/features/paymentProcess/transactionSlice';
import SoftHider from '@/components/softHider/SoftHider';
import ButtonLoader from '@/components/loader/ButtonLoader';
import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';

import { usePaypalCreate } from './usePaypalCreate';
import { usePaypalExecute } from './usePaypalExecute';

import { PaypalButtonWrapper, PaypalButtonVisualContainer } from './PaypalButton.style';

const paypalButtonStyle: PayPalButtonsComponentProps['style'] = {
  layout: 'horizontal',
  color: 'black',
  shape: 'rect',
  label: 'pay',
  tagline: false,
  disableMaxWidth: true,
  height: 46,
};

function PaypalButton({
  disabled = false,
  onClick = () => {},
  setProcessing = () => {},
  testId = 'paymentSelectPaypal'
}: {
  disabled?: boolean,
  onClick?: () => void,
  setProcessing?: (newProcessing: boolean) => void,
  testId?: string,
} = {}) {
  const dispatch = useAppDispatch();

  const [ready, setReady] = useState(false);

  const journeyNavigate = useJourneyNavigate();
  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();

  const providersLibs = useContext(ProvidersLibsContext);

  const { triggerCreateApi } = usePaypalCreate();
  const { triggerExecuteApi } = usePaypalExecute();

  // Wait for paypal options (including provider config)
  if (!providersLibs?.paypalOptions) {
    return <ButtonLoader />;
  }

  // API Reference: https://developer.paypal.com/sdk/js/reference/#link-createorder
  const onCreateOrder: PayPalButtonsComponentProps['createOrder'] = async () => {
    onClick();
    setProcessing(true);

    return triggerCreateApi({
      onSuccess: () => {},
      onError: () => {
        setProcessing(false);
        dispatch(setErrorMessage('Error creating Paypal payment'));
        journeyNavigate('error');
      }
    });
  };

  // API Reference: https://developer.paypal.com/sdk/js/reference/#link-oninitonclick
  const onInit: PayPalButtonsComponentProps['onInit'] = () => {
    setReady(true);
  };

  // API Reference: https://developer.paypal.com/sdk/js/reference/#link-onapprove
  const onApprove: PayPalButtonsComponentProps['onApprove'] = async (data) => {
    await triggerExecuteApi(data.paymentID, data.payerID, {
      onSuccess: (responseData) => {
        const firstName = responseData?.data?.firstName;
        if (firstName) {
          dispatch(updateUserDetailsFields({ firstName }));
        }
        setProcessing(false);
        dispatch(setComplete(true));
        dispatch(clearErrorMessage());
        paymentSuccess();
      },
      onError: () => {
        setProcessing(false);
        dispatch(setErrorMessage('Error executing Paypal payment'));
        paymentFailure();
      },
    });
  };

  const onCancel: PayPalButtonsComponentProps['onCancel'] = () => {
    setProcessing(false);
    logger.info('GooglePay cancelled');
  };

  const onError: PayPalButtonsComponentProps['onError'] = (error) => {
    logger.error('Error on Paypal');
    logger.error(error);

    setProcessing(false);
    dispatch(setErrorMessage('Error on Paypal'));
    paymentFailure();
  };

  return (
    <SoftHider show={ready} loader={<ButtonLoader />}>
      <PaypalButtonWrapper data-test={testId}>
        <PaypalButtonVisualContainer>
          <PayPalButtons
            style={paypalButtonStyle}
            disabled={disabled}
            createOrder={onCreateOrder}
            onInit={onInit}
            onApprove={onApprove}
            onCancel={onCancel}
            onError={onError}
          />
        </PaypalButtonVisualContainer>
      </PaypalButtonWrapper>
    </SoftHider>
  );
}

export default PaypalButton;
